import Dialog, { DialogProps } from '@mui/material/Dialog';
import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, DialogActions, DialogContent, DialogContentText, Divider, Grid, List, ListItem, ListItemText, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useAtom } from 'jotai';
import { assignTeamMembersAtom, selectedOperationIdsAtom, openAssignmentDialogAtom, syncTeamMembersAtom, enableAssignmentButtonAtom } from 'Atoms/AssignmentAtoms';
import { useFetchTeam, useFetchTeamNames } from 'hooks/TeamsHooks';
import { AutoCompleteValues } from "types/AutoCompleteValues";
import MuiDropdown from 'components/WorkAssignmnetGrid/MuiDropDown';
import { AssignmentColumns } from './AssignmentColumns';
import { useAddAssignments, useDeleteAssignments} from 'hooks/AssignmentHooks';
import { Assignments } from 'types/Assignments';
import { Assignment } from 'types/Assignment';
import { useFetchsettings, useUpdateSettings } from 'hooks/SettingHooks';
import { OwnerIdAtom } from 'Atoms/AuthAtoms';
import { SettingKey } from 'constants/SettingKey';
import { Settings } from 'types/Settings';
import { SortByName } from 'components/Common/SortModel';
import { BoxStyle } from './BoxStyle';
import { TableStyle } from './AssignmentStyles';
import { WaFunctionsGetSyncOperationToDays } from 'constants/Constants';
import { useFetchConfiguration } from 'hooks/ConfigurationHooks';
import { useTranslation } from "react-i18next";
import { selectedTeamAtom } from 'Atoms/AssignmentAtoms';

type Args = {
  AssignmentType: string;
};

const AssignmentDialog = ({AssignmentType}: Args) => {

  const [showAssignDialog, setShowAssignDialog] = useAtom(openAssignmentDialogAtom);
  const [t, ] = useTranslation();
  const [assignedMembers, setAssignedMembers] = useAtom(assignTeamMembersAtom);
  const [syncMembers, setSyncMembers] = useAtom(syncTeamMembersAtom);
  const [selectedOperations, ] = useAtom(selectedOperationIdsAtom);
  const [enableConfirm, ] = useAtom(enableAssignmentButtonAtom);
  const [OwnerId,] = useAtom(OwnerIdAtom);
  const [selectedTeam, setSelectedTeam] = useAtom(selectedTeamAtom);

  const { data: settings } = useFetchsettings(OwnerId,SettingKey.TeamLastUsed);

  const addAssignmentMutation = useAddAssignments(+selectedTeam);
  const deleteAssignmentMutation = useDeleteAssignments();
  const {data: teamNames} = useFetchTeamNames(OwnerId);
  const {data: team } = useFetchTeam(+selectedTeam, true);
  const {data: noOfDaysConfig } = useFetchConfiguration(WaFunctionsGetSyncOperationToDays);
  const [maxWidth] = React.useState<DialogProps['maxWidth']>('lg');
  const [ownerId] = useAtom(OwnerIdAtom);
  const updateSettingsMutation = useUpdateSettings();

  const values = teamNames?.map(t => {
    const value: AutoCompleteValues ={
      id: t.id.toString(),
      name: t.name,
    }
    return value;
  });

  const hasOperationsInFuture = selectedOperations.filter(o => o.isOutsideSyncWindowInFuture).length > 0;
  const noOfDaysInFuture = noOfDaysConfig?.value ? +noOfDaysConfig.value : 0;

  useEffect(() => {
    if (settings && settings.length > 0) {
      setSelectedTeam(settings[0].settingValue);
    }
  }, [settings, values]);

 const handleClose = () => {
    setAssignedMembers([]);
    setSyncMembers([]);
    setShowAssignDialog(false);
  };

  useEffect(() => {
    if (selectedTeam && settings && settings.length > 0) {
      const updatedSettings: Settings = {
        ...settings[0],
        settingValue: selectedTeam,
      };
      updateSettingsMutation.mutate(updatedSettings);
    }
  }, [selectedTeam, settings, updateSettingsMutation]);

 const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    let assignments: Assignments = { Assignments: []}
    const membersToPost = team?.teamMembers.filter((member) =>
    assignedMembers.includes(member.id) || syncMembers.includes(member.id))
    selectedOperations.forEach((op) => {
      membersToPost?.forEach((member) => {
        const assignment: Assignment = {
          assign: assignedMembers.includes(member.id),
          sync: syncMembers.includes(member.id),
          operationId: +op.id,
          userId: member.id,
          teamId: +selectedTeam,
          userName: member.name,
          createdByOwnerId: OwnerId,
          deletedByOwnerId: undefined,
          id: 0
        }
        assignments.Assignments.push(assignment)
      });
    });
    addAssignmentMutation.mutate(assignments);
    handleClose();
  };

  const handleUnassign: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    const assignmentsToDelete = selectedOperations
    .flatMap(op => op.assignedUsers)
    .map(assignment => ({
      ...assignment,
      deletedByOwnerId: ownerId,
    })); 
    deleteAssignmentMutation.mutate({ Assignments: assignmentsToDelete });
    handleClose();
  };

  const listItems = () => {
    return selectedOperations.map((op) => (
      <ListItem sx={{ display: t("listItemText"), marginLeft: 4 }} disableGutters>
        <ListItemText
          primary={op.functionalLocation + '-' + op.workOrderDescription}
        />
      </ListItem>
      )
    )
  }

  return (
    <div>
      <Dialog
        fullWidth={true}
        maxWidth={maxWidth}
        open={showAssignDialog && AssignmentType === "Assign"}
        keepMounted={false}  
        aria-labelledby="responsive-dialog-title">
        <DialogContent>
          <DialogContentText>
            <Grid item container xs={12}>
              <Grid item xs={12} md={12} marginTop={0}>
                <Typography variant="subtitle1" color={'black'} fontWeight={600}>
                  {t("AssignOperationPluralText")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={5.8} marginTop={1}>
              {
                <MuiDropdown
                  options={values ?? []}
                  label={t("SelectTeamText")}
                  onChange={setSelectedTeam}
                  selectedValue={selectedTeam}
                  required={true}
                />
              }
              </Grid>
              <Grid item xs={12} md={0.2} marginTop={1} />
              <Grid item xs={12} md={6} marginTop={1}>
                <Box sx={BoxStyle}>
                  <Typography sx={{ marginTop:1, marginLeft: 2}} variant="subtitle2" color={'black'} fontWeight={600}>
                   {t('OperationDetailsText')}
                  </Typography>
                  <Divider variant="middle" />
                  <List sx={{ listStyleType: 'disc'}} dense={true}>
                    {listItems()}
                  </List>
                </Box>
              </Grid>
              <Grid item xs={12} md={12} marginTop={2}>
              {hasOperationsInFuture && (
                  <Alert severity="warning">
                    <AlertTitle>
                      {t('OperationInFutureAlertTitleText')}
                    </AlertTitle>
                      {noOfDaysInFuture < 1? t('OperationInFutureAlertMessage'):
                        t('OperationInFutureAlertMessageWithDaysPrefixText') + noOfDaysConfig?.value + t('OperationInFutureAlertMessageWithDaysSuffixText')}
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={12} marginTop={2} style={{ height: '40vh' }}>
                <DataGridPro
                  sx={TableStyle}
                  disableColumnMenu
                  hideFooter
                  rows={team?.teamMembers.filter(m => m.isActive) ?? []}
                  columns={AssignmentColumns()}
                  initialState={{
                    sorting: {
                      sortModel: [SortByName]
                    }
                  }}
                  localeText={{
                      noRowsLabel: t('NoRowsText')
                  }}
                />
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
           {t("GenericCancelText")}
          </Button>
          <Button variant="contained"  disabled={!enableConfirm} color="primary" autoFocus onClick={handleConfirm}>
           {t("ConfirmCapitalisedText")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth={true}
        maxWidth='sm'
        open={showAssignDialog && AssignmentType === "Unassign" }
        keepMounted={false}  
        aria-labelledby="responsive-dialog-title">
        <DialogContent>
          <DialogContentText>
              <Typography variant="subtitle1" color={'black'} fontWeight={600} marginBottom={2}>
                  {t("UnassignOperationDialog")}
              </Typography>  
              <Typography variant="body2" color={'black'} fontWeight={400} marginBottom={2}>
                {t("UnassignConfirmationMessage")}
              </Typography>
              <Typography variant="body2" color={'black'} fontWeight={400} >
                {t("ContinuationMessage")}
              </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
           {t("GenericCancelText")}
          </Button>
          <Button variant="contained"  color="primary" autoFocus onClick={handleUnassign}>
           {t("ConfirmCapitalisedText")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
export default AssignmentDialog;
